import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import { isSameDay, STATUS_PALETTE, formatDuration } from "../lib/appointments";
import TickProgressBar from "../components/ProgressBar";

/**
 * Module F — App do Colaborador (Simulador de bolso)
 * Smartphone-bezel mockup with the professional's daily schedule, live status
 * actions (Iniciar / Concluir) and the progress bar — fully wired to the central
 * appointments store, so any update reflects on Agenda and Terminal in real-time.
 */
const EmployeeApp = () => {
    const { brand, professionals, appointments, startAppointment, finishAppointment } = useSalon();
    const [activeProId, setActiveProId] = useState(professionals[0]?.id || null);
    const [now, setNow] = useState(new Date());

    const pro = useMemo(
        () => professionals.find((p) => p.id === activeProId) || professionals[0],
        [professionals, activeProId]
    );

    // Live clock + minute tick for progress bars
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    const todayAppts = useMemo(() => {
        if (!pro) return [];
        return appointments
            .filter((a) => a.professionalId === pro.id && isSameDay(a.date, now) && a.status !== "cancelado")
            .sort((a, b) => a.date - b.date);
    }, [appointments, pro, now]);

    const stats = useMemo(() => {
        const inProgress = todayAppts.find((a) => a.status === "em-andamento");
        const next = todayAppts.find((a) => a.status === "agendado");
        const done = todayAppts.filter((a) => a.status === "concluido").length;
        const revenue = todayAppts
            .filter((a) => a.status === "concluido")
            .reduce((s, a) => s + (a.servicePrice || 0), 0);
        return { inProgress, next, done, total: todayAppts.length, revenue };
    }, [todayAppts]);

    const hh = now.getHours().toString().padStart(2, "0");
    const mm = now.getMinutes().toString().padStart(2, "0");

    if (!pro) return null;

    return (
        <div data-testid="employee-app-mockup" className="grid lg:grid-cols-[1fr_400px] gap-8 items-start min-h-[calc(100vh-130px)] px-4">
            {/* LEFT: Pitch + selector */}
            <div className="space-y-6 max-w-xl">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">
                        Módulo · Aplicativo de bolso
                    </div>
                    <h2 className="salon-heading text-3xl md:text-4xl text-white mt-2">
                        O profissional comanda. <br />
                        <span style={{ color: brand.primaryColor }}>Direto da cadeira.</span>
                    </h2>
                    <p className="text-amber-200/70 mt-4 text-sm leading-relaxed">
                        Agenda do dia em tempo real, com botões de <strong>Iniciar Serviço</strong> e <strong>Concluir</strong> que disparam eventos para o Hub Central da Agenda e o Terminal Balcão.
                        A barra de progresso muda de cor conforme o tempo passa — <span className="text-emerald-300">verde</span> em dia, <span className="text-amber-300">âmbar</span> atenção, <span className="text-red-300">vermelha</span> atrasado.
                    </p>
                </div>

                {/* Pro selector */}
                <div data-testid="empapp-pro-switcher" className="gold-panel p-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/60 mb-2 font-bold">
                        Simulando o app de:
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {professionals.filter((p) => p.active !== false).map((p) => {
                            const active = p.id === pro.id;
                            return (
                                <button
                                    key={p.id}
                                    data-testid={`empapp-pro-opt-${p.id}`}
                                    onClick={() => setActiveProId(p.id)}
                                    className={`rounded-xl border p-2.5 text-left transition-all min-h-[60px] flex items-center gap-2 ${
                                        active
                                            ? "border-amber-400/60 bg-amber-500/15"
                                            : "border-amber-500/15 bg-black/40 hover:border-amber-500/40"
                                    }`}
                                >
                                    <img src={p.avatar} alt="" className="h-8 w-8 rounded-lg object-cover ring-1 ring-amber-500/30" />
                                    <div className="min-w-0">
                                        <div className="text-xs font-bold text-amber-100 truncate">{p.name}</div>
                                        <div className="text-[10px] text-amber-300/60 truncate">{p.role}</div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Day stats */}
                <div className="grid grid-cols-2 gap-3" data-testid="empapp-stats">
                    <StatCard label="Atendimentos hoje" value={stats.total} icon="fa-calendar-day" />
                    <StatCard label="Concluídos" value={stats.done} icon="fa-circle-check" />
                    <StatCard label="Em andamento" value={stats.inProgress ? "1" : "0"} icon="fa-hourglass-half" highlight={!!stats.inProgress} />
                    <StatCard label="Faturado hoje" value={`R$ ${stats.revenue.toFixed(2)}`} icon="fa-sack-dollar" />
                </div>
            </div>

            {/* RIGHT: phone bezel */}
            <div className="mx-auto">
                <div className="relative" data-testid="empapp-phone">
                    {/* Phone bezel */}
                    <div className="rounded-[44px] p-2 bg-gradient-to-b from-zinc-700 to-zinc-900 shadow-2xl ring-1 ring-zinc-800">
                        <div className="rounded-[36px] overflow-hidden bg-black w-[340px] h-[680px] flex flex-col relative">
                            {/* Notch */}
                            <div className="absolute top-2 left-1/2 -translate-x-1/2 h-4 w-24 bg-black rounded-full z-10"></div>

                            {/* Status bar */}
                            <div className="px-6 pt-3 pb-1 flex items-center justify-between text-[10px] font-mono text-amber-100/80">
                                <span data-testid="empapp-clock">{hh}:{mm}</span>
                                <div className="flex items-center gap-1.5">
                                    <i className="fa-solid fa-signal text-[8px]"></i>
                                    <i className="fa-solid fa-wifi text-[8px]"></i>
                                    <i className="fa-solid fa-battery-three-quarters text-[10px]"></i>
                                </div>
                            </div>

                            {/* App header */}
                            <div className="px-5 pt-3 pb-4 flex items-center gap-3 border-b border-amber-500/15">
                                <img src={pro.avatar} alt="" className="h-10 w-10 rounded-xl object-cover ring-1 ring-amber-500/30" />
                                <div className="min-w-0 flex-1">
                                    <div className="text-[10px] uppercase tracking-wider text-amber-300/60">Olá,</div>
                                    <div className="text-sm font-bold text-amber-100 truncate salon-heading">{pro.name.split(" ")[0]}</div>
                                </div>
                                <button className="h-9 w-9 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300">
                                    <i className="fa-solid fa-bell text-xs"></i>
                                </button>
                            </div>

                            {/* In-progress hero */}
                            {stats.inProgress && (
                                <div className="px-4 pt-3" data-testid="empapp-hero-running">
                                    <RunningCard
                                        appt={stats.inProgress}
                                        onFinish={() => {
                                            finishAppointment(stats.inProgress.id);
                                            toast.success(`Concluído · ${stats.inProgress.clientName}`);
                                        }}
                                    />
                                </div>
                            )}

                            {/* Day list */}
                            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5" data-testid="empapp-day-list">
                                <div className="flex items-center justify-between mb-1">
                                    <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-300/70">Agenda do dia</h3>
                                    <span className="text-[9px] text-amber-200/50 font-mono">
                                        {now.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}
                                    </span>
                                </div>

                                {todayAppts.length === 0 && (
                                    <div className="py-8 text-center text-amber-200/40 text-xs">
                                        <i className="fa-solid fa-mug-hot text-2xl block mb-2"></i>
                                        Nenhum agendamento para hoje.<br />Aproveita o cafezinho.
                                    </div>
                                )}

                                {todayAppts.map((a) => (
                                    <PhoneCard
                                        key={a.id}
                                        appt={a}
                                        onStart={() => {
                                            startAppointment(a.id);
                                            toast.success(`Iniciado · ${a.clientName}`, {
                                                description: `${a.duration_min} min previstos`,
                                            });
                                        }}
                                        onFinish={() => {
                                            finishAppointment(a.id);
                                            toast.success(`Concluído · ${a.clientName}`);
                                        }}
                                    />
                                ))}
                            </div>

                            {/* Bottom indicator */}
                            <div className="h-6 flex items-center justify-center">
                                <div className="h-1 w-24 bg-amber-200/40 rounded-full"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Running (hero) card ──────────────────────────────────────────────────
const RunningCard = ({ appt, onFinish }) => (
    <div data-testid={`empapp-running-${appt.id}`} className="rounded-2xl border border-sky-500/50 bg-gradient-to-br from-sky-500/15 to-sky-500/5 p-4 shadow-[0_8px_30px_-12px_rgba(56,189,248,0.4)]">
        <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] uppercase tracking-[0.25em] text-sky-300 font-bold">
                <i className="fa-solid fa-circle text-[7px] mr-1.5 animate-pulse"></i>Em andamento
            </span>
            <span className="font-mono text-[10px] text-sky-200/80">{appt.time}</span>
        </div>
        <div className="text-base font-bold text-sky-100 truncate">{appt.clientName}</div>
        <div className="text-xs text-sky-200/70 truncate">{appt.serviceName} · {formatDuration(appt.duration_min)}</div>
        <div className="mt-3">
            <TickProgressBar appointment={appt} variant="thick" showLabel testId={`empapp-progress-${appt.id}`} />
        </div>
        <button
            data-testid={`empapp-finish-${appt.id}`}
            onClick={onFinish}
            className="mt-3 w-full min-h-[44px] rounded-xl border border-emerald-400/50 bg-emerald-500/20 hover:bg-emerald-500/35 text-emerald-100 text-[11px] uppercase tracking-wider font-bold transition-all"
        >
            <i className="fa-solid fa-check mr-2"></i>Concluir Serviço
        </button>
    </div>
);

// ─── Compact phone card ───────────────────────────────────────────────────
const PhoneCard = ({ appt, onStart, onFinish }) => {
    const palette = STATUS_PALETTE[appt.status] || STATUS_PALETTE.agendado;
    return (
        <div
            data-testid={`empapp-appt-${appt.id}`}
            className={`rounded-xl border ${palette.chipBorder} ${palette.chipBg} p-3`}
        >
            <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span className={`font-mono text-[11px] font-bold ${palette.chipText}`}>{appt.time}</span>
                        <span className={`text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded ${palette.chipText} opacity-80 border ${palette.chipBorder}`}>
                            {palette.label}
                        </span>
                    </div>
                    <div className={`text-xs font-semibold truncate mt-0.5 ${palette.chipText}`}>{appt.clientName}</div>
                    <div className="text-[10px] truncate opacity-70" style={{ color: palette.chipText }}>
                        {appt.serviceName} · {formatDuration(appt.duration_min)}
                    </div>
                </div>
                {appt.status === "agendado" && (
                    <button
                        data-testid={`empapp-start-${appt.id}`}
                        onClick={onStart}
                        className="shrink-0 min-h-[44px] min-w-[44px] h-11 rounded-xl border border-sky-400/50 bg-sky-500/15 hover:bg-sky-500/30 text-sky-200 transition-all"
                        title="Iniciar"
                    >
                        <i className="fa-solid fa-play text-xs"></i>
                    </button>
                )}
                {appt.status === "em-andamento" && (
                    <button
                        data-testid={`empapp-finish-card-${appt.id}`}
                        onClick={onFinish}
                        className="shrink-0 min-h-[44px] min-w-[44px] h-11 rounded-xl border border-emerald-400/50 bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-200 transition-all"
                        title="Concluir"
                    >
                        <i className="fa-solid fa-check text-xs"></i>
                    </button>
                )}
                {appt.status === "concluido" && (
                    <div className="shrink-0 h-11 w-11 rounded-xl flex items-center justify-center text-emerald-400">
                        <i className="fa-solid fa-circle-check"></i>
                    </div>
                )}
            </div>
            {appt.status === "em-andamento" && (
                <div className="mt-2">
                    <TickProgressBar appointment={appt} variant="thin" testId={`empapp-progress-card-${appt.id}`} />
                </div>
            )}
        </div>
    );
};

const StatCard = ({ label, value, icon, highlight }) => (
    <div
        className={`gold-panel p-3 ${
            highlight ? "ring-1 ring-sky-400/40 shadow-[0_0_22px_-12px_rgba(56,189,248,0.5)]" : ""
        }`}
    >
        <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/60">{label}</div>
            <i className={`fa-solid ${icon} text-amber-300/80 text-sm`}></i>
        </div>
        <div
            className="salon-heading text-xl mt-1"
            style={{ color: highlight ? "#7dd3fc" : "#fde7a8" }}
        >
            {value}
        </div>
    </div>
);

export default EmployeeApp;
